import { useQueryClient } from "@tanstack/react-query";
import PQueue from "p-queue";
import { useState } from "react";
import {
  getGetTemplateListQueryKey,
  useCompleteReportSampleUpload,
  useGetReportSamplePartUrl,
  useStartReportSampleUpload,
} from "~/api/custom-reports/custom-reports";
import { uploadChunkToS3 } from "~/lib/s3";
import logger from "~/utils/logger";

const MAX_CONCURRENT_CHUNKS = 6;
const CHUNK_SIZE = 6 * 1024 * 1024; // 6MB - AWS S3 requires minimum 5MB for multipart uploads (except last part)

interface UseReportSampleUploadParams {
  organizationId: string;
}

export const useReportSampleUpload = ({
  organizationId,
}: UseReportSampleUploadParams) => {
  const { mutateAsync: startReportSampleUpload } = useStartReportSampleUpload();
  const { mutateAsync: getReportSamplePartUrl } = useGetReportSamplePartUrl();
  const { mutateAsync: completeReportSampleUpload } =
    useCompleteReportSampleUpload();
  const queryClient = useQueryClient();

  const [uploadQueue] = useState(
    () => new PQueue({ concurrency: MAX_CONCURRENT_CHUNKS })
  );

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadReportSample = async (file: File): Promise<void> => {
    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Generate a unique uploadId for this upload
      const uploadId = crypto.randomUUID();

      logger.info("🔄 Starting report sample upload", {
        filename: file.name,
        uploadId,
      });

      // Set initial progress
      setUploadProgress(10);

      // Start multipart upload
      const startResp = await startReportSampleUpload({
        organizationId,
        uploadId,
        data: {
          contentType: file.type as
            | "application/pdf"
            | "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            | "image/png"
            | "image/jpeg",
          filename: file.name,
        },
      });

      const { uploadId: s3UploadId, key } = startResp;

      setUploadProgress(20);

      // Calculate chunks
      const chunkCount = Math.ceil(file.size / CHUNK_SIZE);
      const uploadedParts: { ETag: string; PartNumber: number }[] = [];

      const uploadChunk = async (index: number) => {
        const partNumber = index + 1;
        const start = index * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);

        const partUrlData = await getReportSamplePartUrl({
          organizationId,
          uploadId,
          data: {
            key,
            uploadId: s3UploadId,
            partNumber,
          },
        });

        const etagHeader = await uploadChunkToS3(
          partUrlData.signedUrl as string,
          chunk,
          file.type,
          undefined
        );

        if (!etagHeader) {
          throw new Error("Missing ETag in S3 response");
        }

        uploadedParts.push({
          ETag: etagHeader.replace(/"/g, ""),
          PartNumber: partNumber,
        });

        // Progress from 20% to 80% during upload
        const progressPct = 20 + Math.round((partNumber / chunkCount) * 60);
        setUploadProgress(progressPct);
      };

      // Queue and upload all chunks
      const chunkPromises: Promise<void>[] = [];
      for (let i = 0; i < chunkCount; i++) {
        chunkPromises.push(
          uploadQueue.add(() => uploadChunk(i), { priority: 0 })
        );
      }
      await Promise.all(chunkPromises);

      setUploadProgress(85);

      // Complete the upload
      await completeReportSampleUpload({
        organizationId,
        uploadId,
        data: {
          key,
          uploadId: s3UploadId,
          filename: file.name,
          idempotencyKey: uploadId,
          parts: uploadedParts.sort((a, b) => a.PartNumber - b.PartNumber),
        },
      });

      setUploadProgress(95);

      // Invalidate templates list to refresh after upload
      await queryClient.invalidateQueries({
        queryKey: getGetTemplateListQueryKey(organizationId),
      });

      setUploadProgress(100);

      logger.info("✅ Report sample upload completed", {
        filename: file.name,
        uploadId,
      });
    } catch (error) {
      logger.error(`❌ Error uploading report sample ${file.name}:`, error);
      throw error;
    } finally {
      setIsUploading(false);
      // Reset progress after a short delay
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  return {
    uploadReportSample,
    isUploading,
    uploadProgress,
  };
};
